// Shared "daily lap" model for the ENGAGE and GROW task flows.
//
// WHY THIS FILE EXISTS
// Engage and Grow used to derive their donut progress and their pause nudges independently:
// Engage had a two-lap visualisation (0-25 brand colours, then a reset to 0% for a red
// "second lap" up to 50) plus two modals, while Grow saturated at 100% for the rest of the
// day and fired a single modal. Same product idea, two implementations, two sets of bugs.
// Everything about HOW A LAP LOOKS AND WHEN A NUDGE FIRES now lives here, so the two flows
// cannot drift again.
//
// WHAT IS *NOT* UNIFIED (deliberately)
// The COUNTING stays per-flow, because the limits genuinely differ:
//   - ENGAGE: lap size is a frontend constant (DAILY_ENGAGE_TARGET, mirrors the backend's
//     own DAILY_ENGAGE_TARGET, which it does not send as a discrete field). Soft only.
//   - GROW:   lap size is the server-sent growLimits.dailyTarget, and grow additionally has
//     HARD weekly/monthly caps (growLimits.weeklyHard / monthlyHard, growBlockedUntil) that
//     are enforced server-side and are untouched by this model.
// Both daily targets are SOFT on the backend — gcGetTasks keeps serving CAMPAIGN and CONNECT
// tasks past them; only the weekly/monthly connect caps ever stop the feed. That is what
// makes a second lap reachable for Grow rather than dead UI.

// A "day" is two laps for both flows. Past the second lap the ring stays full and the
// break nudge has already fired; we do not start a third lap.
export const LAPS_PER_DAY = 2;

// Must mirror gcGetTasks/src/index.js DAILY_ENGAGE_TARGET (the backend only sends the
// grow-side target as a discrete field, via growLimits.dailyTarget).
export const DAILY_ENGAGE_TARGET = 25;

// Pre-fetch fallback for the grow lap size; the real value arrives in growLimits.dailyTarget.
export const DEFAULT_GROW_DAILY_TARGET = 20;

// Must mirror amplify/backend/function/gcUpdateTask/src/schedule.js MAX_SCHEDULE_AHEAD_DAYS
// (the source of truth; Lambda code is CommonJS outside the CRA source root, so we mirror
// by value exactly like DAILY_ENGAGE_TARGET above).
export const MAX_SCHEDULE_AHEAD_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Coverage-through-tomorrow: lastScheduledFor >= start of TOMORROW in UTC.
 *  Matches gcGetTasks' postPercent rule (step 29). Garbage/absent -> false. */
export function isPostCoveredThroughTomorrow(lastScheduledFor, now = new Date()) {
    if (!lastScheduledFor) return false;
    const lastMs = new Date(lastScheduledFor).getTime();
    if (!Number.isFinite(lastMs)) return false;
    const startOfTomorrowUtc =
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) + DAY_MS;
    return lastMs >= startOfTomorrowUtc;
}

/** Mirror of schedule.js isScheduleCapped: coverage already >= now + 14 days. */
export function isPostScheduleCapped(lastScheduledFor, now = new Date()) {
    if (!lastScheduledFor) return false;
    const lastMs = new Date(lastScheduledFor).getTime();
    if (!Number.isFinite(lastMs)) return false;
    return lastMs >= now.getTime() + MAX_SCHEDULE_AHEAD_DAYS * DAY_MS;
}

// Nudge variants. One vocabulary for both flows — the kind ("engage" | "grow") only selects
// the wording, never the trigger logic.
export const NUDGE_LAP_COMPLETE = "lap-complete"; // finished lap 1 -> celebrate, offer lap 2
export const NUDGE_DAY_COMPLETE = "day-complete"; // finished lap 2 -> ask for a break

function toCount(value) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function toLapSize(value, fallback) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
}

function clampPercent(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.min(Math.floor(n), 100);
}

/**
 * The single source of truth for how far round the ring is, which lap we are on, and how the
 * centre label reads — for BOTH flows.
 *
 * @param completed     tasks completed today for this flow (optimistic client count)
 * @param lapSize       tasks per lap (25 for engage, growLimits.dailyTarget for grow)
 * @param serverPercent server-computed daily percent, used ONLY as the pre-count fallback
 *                      (i.e. when `completed` is 0, typically the moment before the first
 *                      stats response has populated completedToday)
 */
export function getLapState({ completed, lapSize, serverPercent = 0 } = {}) {
    const size = toLapSize(lapSize, DAILY_ENGAGE_TARGET);
    const done = toCount(completed);
    const dayTotal = size * LAPS_PER_DAY;

    const isDayComplete = done >= dayTotal;
    // Exactly `size` is still lap 1 showing 100% — the lap only flips on the NEXT task, so
    // the user gets to see their full ring before it resets.
    const lapIndex = isDayComplete ? LAPS_PER_DAY : done > size ? 2 : 1;
    const isFinalLap = lapIndex >= LAPS_PER_DAY;

    const completedInLap = isDayComplete
        ? size
        : lapIndex === 2
            ? done - size
            : done;

    const visualPercent = done === 0
        ? clampPercent(serverPercent)
        : Math.min(Math.floor((completedInLap / size) * 100), 100);

    return {
        lapSize: size,
        lapsPerDay: LAPS_PER_DAY,
        dayTotal,
        completed: done,
        lapIndex,
        isFinalLap,
        isDayComplete,
        completedInLap,
        remainingInLap: Math.max(0, size - completedInLap),
        remainingToday: Math.max(0, dayTotal - done),
        visualPercent,
    };
}

/**
 * Centre-label text. The superscript marks the second lap; it is the shared signature of the
 * lap model, so GROW gets it on exactly the same terms ENGAGE does.
 */
export function lapLabel(base, lapState) {
    return lapState && lapState.lapIndex > 1 ? `${base} ²` : base;
}

/**
 * Which nudge (if any) a count transition earns. Both flows call this with their own lap size;
 * the thresholds therefore differ while the RULE is identical.
 *
 * Returns NUDGE_DAY_COMPLETE, NUDGE_LAP_COMPLETE, or null. A transition that crosses both
 * boundaries at once yields only the day-complete nudge (the more important message).
 */
export function lapNudgeForCompletion({ previousCompleted, nextCompleted, lapSize } = {}) {
    const size = toLapSize(lapSize, DAILY_ENGAGE_TARGET);
    const prev = toCount(previousCompleted);
    const next = toCount(nextCompleted);
    if (next <= prev) return null;

    const dayTotal = size * LAPS_PER_DAY;
    if (next >= dayTotal && prev < dayTotal) return NUDGE_DAY_COMPLETE;
    if (next >= size && prev < size) return NUDGE_LAP_COMPLETE;
    return null;
}

/**
 * Nudges a given count has ALREADY earned. Used to back-fill the "already shown" set when a
 * count arrives from the server (page reload, profile switch, stats refresh) so we never
 * celebrate a milestone the user passed while the tab was closed.
 */
export function earnedLapNudges({ completed, lapSize } = {}) {
    const size = toLapSize(lapSize, DAILY_ENGAGE_TARGET);
    const done = toCount(completed);
    const earned = [];
    if (done >= size) earned.push(NUDGE_LAP_COMPLETE);
    if (done >= size * LAPS_PER_DAY) earned.push(NUDGE_DAY_COMPLETE);
    return earned;
}

/**
 * UTC calendar day key. The backend buckets completedToday, growLimits.dailyUsed and both
 * daily percents by UTC day (gcGetTasks formatDateUTC), so anything that must reset "at the
 * same moment the counters do" — the nudge memory — has to key off UTC too, not local time.
 */
export function utcDayKey(date = new Date()) {
    return date.toISOString().slice(0, 10);
}
